/**
 * Zod DTOs + response types for the catalog and category domains
 * (spec §5.3/§5.6/§24.3).
 */
import { z } from "zod";

const decimalString = z
  .string()
  .regex(/^-?\d{1,12}(\.\d{1,3})?$/, "Invalid decimal value");
const moneyInput = z
  .union([z.number().finite(), decimalString])
  .transform((v) => String(v));
const nonnegativeMoneyInput = moneyInput.refine(
  (value) => Number(value) >= 0,
  "Amount must be nonnegative",
);
const nonnegativeQuantityInput = z
  .union([z.number().finite().nonnegative(), decimalString])
  .transform(String)
  .refine((value) => Number(value) >= 0, "Quantity must be nonnegative");

const categoryTranslationSchema = z.object({
  name: z.string().trim().min(1).max(120),
});

export const categoryTranslationsSchema = z
  .object({
    de: categoryTranslationSchema.optional(),
    fr: categoryTranslationSchema.optional(),
    es: categoryTranslationSchema.optional(),
  })
  .optional();
export type CategoryTranslationsDto = z.infer<
  typeof categoryTranslationsSchema
>;

export const categoryCreateSchema = z.object({
  parentId: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).max(120),
  sortOrder: z.number().int().min(0).optional(),
  translations: categoryTranslationsSchema,
});
export type CategoryCreateDto = z.infer<typeof categoryCreateSchema>;

/** Alias kept for the frozen contract name. */
export const categoryUpsertSchema = categoryCreateSchema;

export const categoryUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  sortOrder: z.number().int().min(0).optional(),
  active: z.boolean().optional(),
  translations: categoryTranslationsSchema,
});
export type CategoryUpdateDto = z.infer<typeof categoryUpdateSchema>;

export interface CategoryNodeResponse {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  translations: { de?: string; fr?: string; es?: string };
  children: CategoryNodeResponse[];
}

const catalogTranslationSchema = z.object({
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2000).optional(),
});

export const catalogTranslationsSchema = z
  .object({
    de: catalogTranslationSchema.optional(),
    fr: catalogTranslationSchema.optional(),
    es: catalogTranslationSchema.optional(),
  })
  .optional();
export type CatalogTranslationsDto = z.infer<typeof catalogTranslationsSchema>;

const packageItemSchema = z.object({
  itemId: z.string().uuid(),
  quantity: nonnegativeQuantityInput.refine(
    (value) => Number(value) > 0,
    "Quantity must be greater than zero",
  ),
});

export const catalogItemCreateSchema = z
  .object({
    sku: z.string().trim().max(40).optional(),
    barcode: z.string().trim().max(80).optional(),
    kind: z.enum(["item", "package"]).default("item"),
    stockEnabled: z.boolean().default(true),
    defaultLocationId: z.string().uuid().nullable().optional(),
    defaultUnitCost: nonnegativeMoneyInput.optional(),
    reorderPoint: nonnegativeQuantityInput.optional(),
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(2000).optional(),
    unitPrice: moneyInput.default("0"),
    taxRate: moneyInput.default("0"),
    active: z.boolean().optional().default(true),
    sortOrder: z.number().int().min(0).optional(),
    packageItems: z.array(packageItemSchema).default([]),
    translations: catalogTranslationsSchema,
  })
  .superRefine((value, ctx) => {
    if (value.kind === "item" && value.packageItems.length) {
      ctx.addIssue({
        code: "custom",
        path: ["packageItems"],
        message: "Only packages can contain items",
      });
    }
    if (
      new Set(value.packageItems.map((component) => component.itemId)).size !==
      value.packageItems.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["packageItems"],
        message: "Package items must be unique",
      });
    }
  });
export type CatalogItemCreateDto = z.infer<typeof catalogItemCreateSchema>;

export const catalogItemUpsertSchema = catalogItemCreateSchema;

export const catalogItemUpdateSchema = z.object({
  sku: z.string().trim().max(40).nullable().optional(),
  barcode: z.string().trim().max(80).nullable().optional(),
  kind: z.enum(["item", "package"]).optional(),
  stockEnabled: z.boolean().optional(),
  defaultLocationId: z.string().uuid().nullable().optional(),
  defaultUnitCost: nonnegativeMoneyInput.nullable().optional(),
  reorderPoint: nonnegativeQuantityInput.nullable().optional(),
  name: z.string().trim().min(1).max(160).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  unitPrice: moneyInput.optional(),
  taxRate: moneyInput.optional(),
  active: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  packageItems: z.array(packageItemSchema).optional(),
  translations: catalogTranslationsSchema,
});
export type CatalogItemUpdateDto = z.infer<typeof catalogItemUpdateSchema>;

export interface CatalogItemResponse {
  id: string;
  sku?: string;
  barcode?: string;
  kind: "item" | "package";
  stockEnabled: boolean;
  defaultLocationId?: string;
  defaultLocation?: { id: string; code: string; name: string };
  defaultUnitCost?: string;
  reorderPoint?: string;
  name: string;
  description?: string;
  unitPrice: string;
  taxRate: string;
  active: boolean;
  sortOrder: number;
  packageItems: Array<{
    itemId: string;
    quantity: string;
    item: { id: string; name: string; sku?: string };
  }>;
  translations: {
    de?: { name: string; description?: string };
    fr?: { name: string; description?: string };
    es?: { name: string; description?: string };
  };
  inventory?: {
    onHand: string;
  };
  createdAt: string;
  updatedAt: string;
}
